import { useState, useEffect } from "react";
import { menuItems } from "../../data/rubriqueMenu.data";
import arrowIcon from "../../assets/icons/arrow.png";
import { Link, useNavigate, useLocation } from "react-router-dom";
import routes from "../../routes/routes";
import { getAllRubriques } from "../../services/rubrique.service";

const Sidebar = ({ headerHeight = 0 }) => {
    const [showMobileCategories, setShowMobileCategories] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const [rubriques, setRubriques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

    useEffect(() => {
        const fetchRubriques = async () => {
            try {
                setLoading(true);
                const response = await getAllRubriques();
                if (response && response.data && Array.isArray(response.data)) {
                    setRubriques(response.data);
                } else if (response && Array.isArray(response)) {
                    setRubriques(response);
                } else {
                    console.error("Format de réponse API inattendu:", response);
                    throw new Error("Format de réponse API inattendu");
                }
                setError(false);
            } catch (err) {
                console.error("Erreur lors de la récupération des rubriques:", err);
                setError(true);
                setRubriques([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRubriques();
    }, []);

    // FONCTION DE TRI ALPHABÉTIQUE
    const sortItemsAlphabetically = (items) => {
        return [...items].sort((a, b) => {
            const labelA = (a.name_rub || a.label || '').toLowerCase();
            const labelB = (b.name_rub || b.label || '').toLowerCase();
            return labelA.localeCompare(labelB, 'fr');
        });
    };

    // Utilisez les rubriques de l'API ou fallback aux menuItems statiques, puis triez-les
    const displayItems = sortItemsAlphabetically(rubriques.length > 0 ? rubriques : menuItems);

    useEffect(() => {
        const currentPath = location.pathname;
        if (currentPath.startsWith('/rubrique/')) {
            const currentRubrique = currentPath.split('/').pop();
            const matchingItem = rubriques.length > 0
                ? rubriques.find(item =>
                    item.name_rub?.toLowerCase().replace(/\s+/g, '_') === currentRubrique)
                : menuItems.find(item =>
                    item.label?.toLowerCase().replace(/\s+/g, '_') === currentRubrique);

            if (matchingItem) {
                setActiveItem(matchingItem.name_rub || matchingItem.label);
            }
        }
    }, [location.pathname, rubriques]);

    useEffect(() => {
        const handleScroll = () => {
            const position = window.pageYOffset;
            if (position > headerHeight + 50) {
                setShowMobileCategories(true);
            } else {
                setShowMobileCategories(false);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [headerHeight]);

    const handleItemClick = (item) => {
        const itemLabel = item.name_rub || item.label;
        setActiveItem(itemLabel);
        const rubriqueId = itemLabel.toLowerCase().replace(/\s+/g, '_');
        navigate(`${routes.rubrique}/${rubriqueId}`);
    };

    const getImagePath = (photoPath) => {
        if (!photoPath) return '';
        if (photoPath.startsWith('http')) return photoPath;
        const cleanBaseUrl = REACT_APP_BASE_URL.endsWith('/')
            ? REACT_APP_BASE_URL.slice(0, -1)
            : REACT_APP_BASE_URL;
        return `${cleanBaseUrl}/${photoPath}`;
    };

    return (
        <>
            {/* Mobile keywords design */}
            <div className={`
                xl:hidden
                fixed left-0 right-0 z-30
                bg-greenlogo shadow-sm
                transition-all duration-300 ease-in-out
                ${showMobileCategories ? 'translate-y-0' : '-translate-y-full'}
            `}
                style={{
                    top: `${headerHeight}px`,
                }}
            >
                <div className="overflow-x-auto py-3 px-4 scrollbar-hide">
                    {loading ? (
                        <div className="flex justify-center items-center h-10">
                            <div className="w-6 h-6 border-2 border-greenprimary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <div className="flex gap-2 min-w-max pb-0.5">
                            {displayItems.map((item) => {
                                const itemLabel = item.name_rub || item.label;
                                const isActive = activeItem === itemLabel;
                                return (
                                    <button
                                        key={item.id_rub || itemLabel}
                                        className={`
                                            px-3 py-1.5 rounded-full text-xs whitespace-nowrap
                                            transition-all duration-200
                                            ${isActive
                                                ? 'bg-greenprimary text-white font-medium shadow-sm'
                                                : 'bg-white/90 text-greenprimary hover:bg-white'}
                                        `}
                                        onClick={() => handleItemClick(item)}
                                    >
                                        {itemLabel}
                                    </button>
                                );
                            })}
                            <Link
                                className="px-3 py-1.5 rounded-full text-xs bg-white/90 text-greenprimary hover:bg-white whitespace-nowrap"
                                to={routes.article}
                            >
                                Plus +
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Desktop sidebar */}
            <aside className="
                hidden xl:block xl:relative
                fixed top-0 left-0 h-screen w-80 bg-greensecondary p-6
                border-r-4 border-greenprimary overflow-hidden
            ">
                <div className="mb-6">
                    <h2 className="text-3xl tracking-tighter font-semibold text-gray-900">
                        MAD<span className="text-greenlogo">ATLAS</span><span className="text-danger">.</span>
                    </h2>
                    <p className="text-[9px] text-gray-600 mt-1">
                        Cartes, images et données géographiques de Madagascar, de l'Océan Indien et des Afriques
                    </p>
                </div>
                <div className="relative h-px mb-2">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-greenprimary to-transparent"></div>
                </div>
                <nav className="py-4 overflow-y-auto max-h-[calc(100vh-250px)]">
                    {loading ? (
                        <div className="flex justify-center items-center h-40">
                            <div className="w-8 h-8 border-3 border-greenprimary border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : error ? (
                        <div className="text-center p-4 text-red-500">
                            <p>Impossible de charger les rubriques</p>
                            <button
                                className="mt-2 text-sm text-greenprimary underline"
                                onClick={() => window.location.reload()}
                            >
                                Réessayer
                            </button>
                        </div>
                    ) : (
                        displayItems.map((item, index) => {
                            const itemLabel = item.name_rub || item.label;
                            const itemIcon = item.photo_rub ? getImagePath(item.photo_rub) : item.icon;
                            const isActive = activeItem === itemLabel;
                            const isLast = index === displayItems.length - 1;

                            return (
                                <div key={item.id_rub || itemLabel} className="group">
                                    <div
                                        onClick={() => handleItemClick(item)}
                                        className={`
                                            flex items-center gap-6 font-bold text-sm cursor-pointer py-5 pl-10
                                            transition-colors duration-200 rounded-l-md relative
                                            ${isActive
                                                ? 'text-black'
                                                : 'text-greenprimary hover:text-green-700'}
                                        `}
                                    >
                                        <div className="w-5 h-5 flex items-center justify-center">
                                            {itemIcon ? (
                                                <img
                                                    src={itemIcon}
                                                    alt={itemLabel}
                                                    className={`w-5 h-5 object-contain`}
                                                    onError={(e) => {
                                                        e.target.src = '/default-icon.png';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-5 h-5 bg-greenprimary rounded-full opacity-70"></div>
                                            )}
                                        </div>
                                        <span>{itemLabel}</span>
                                        {isActive && (
                                            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 bg-greenlogo rounded-r"></span>
                                        )}
                                    </div>
                                    {!isLast && (
                                        <div className="relative mx-auto w-[85%]">
                                            <div className="h-px bg-greenprimary opacity-70"></div>
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </nav>
                <Link
                    to={routes.search}
                    className="w-full font-semibold bg-greenprimary text-white py-2.5 flex justify-center items-center gap-2 hover:bg-green-700 transition-colors"
                >
                    Tous les articles <img src={arrowIcon} alt="Voir plus" />
                </Link>
            </aside>
        </>
    );
};

export default Sidebar;